import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compact, flow, get, omit } from 'lodash-es';
import Button from 'reactstrap/lib/Button';
import Col from 'reactstrap/lib/Col';
import Row from 'reactstrap/lib/Row';
import { useCoreSrpFilter } from 'site-modules/shared/hooks/core-inventory/use-core-srp-filter';
import { TrackingHandler } from 'client/tracking/handler';
import { MiniInventoryMap } from 'client/tracking/maps/components/mini-inventory/mini-inventory';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { InventoryEntities, InventoryModel } from 'client/data/models/inventory';
import { PageModel } from 'client/data/models/page';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { SCROLL_DURATION, SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { EventToolbox } from 'client/utils/event-toolbox';
import { getPlural } from 'client/utils/plural';
import { getSelectedPaymentType } from 'site-modules/shared/utils/inventory-utils/get-current-payment-type';
import { transformSelectedFacetsForDisplaying } from 'site-modules/shared/utils/inventory/search-filter';
import { getInventoryListTrackingData } from 'site-modules/shared/utils/inventory/srp-utils';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import {
  addFiltersToQuery as addFiltersToQueryDefault,
  EXCLUDED_FILTER_TYPES,
  MINI_INVENTORY_COUNT,
} from 'site-modules/shared/utils/mini-inventory/mini-inventory';
import { srpLinkBuilderWithParamsConversion } from 'site-modules/shared/utils/srp-link-constructor';
import { EngagementHandler } from 'client/engagement-handlers';
import { ACTIONS } from 'client/engagement-handlers/inventory-engagement-handler/constants';
import { InventoryEngagementHandler } from 'client/engagement-handlers/inventory-engagement-handler/inventory-engagement-handler';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';
import { validation } from 'site-modules/shared/components/form-validation/validation';
import { FilterButton } from 'site-modules/shared/components/inventory/filters-button/filters-button';
import { InventoryDependencyContext } from 'site-modules/shared/components/inventory/inventory-dependency/inventory-dependency-context';
import { usurpDependencyContextValue } from 'site-modules/shared/components/inventory/inventory-dependency/usurp-dependency-context-value';
import { UsurpFiltersDrawer } from 'site-modules/shared/components/inventory/usurp-filters-drawer/usurp-filters-drawer';
import { UsurpFilters } from 'site-modules/shared/components/inventory/usurp-filters/usurp-filters';
import { Link } from 'site-modules/shared/components/link/link';
import { LoadingSpinner } from 'site-modules/shared/components/loading-spinner/loading-spinner';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { StyledZipInput } from 'site-modules/shared/components/styled-zip-input/styled-zip-input';
import { Zip } from 'site-modules/shared/components/zip/zip';
import { MiniInventoryCard } from './mini-inventory-card/mini-inventory-card';

import './mini-inventory-module.scss';

const CREATIVE_ID = 'edm-entry-mini-inventory';
const DISPLAY_INVENTORY_COUNT = { true: 4, false: 3 };

EngagementHandler.register(InventoryEngagementHandler);

function MiniInventoryModuleUI({
  vehicleParams = null,
  vehicleName = null,
  initialFilter,
  excludedFilterTypes = EXCLUDED_FILTER_TYPES,
  addFiltersToQuery = addFiltersToQueryDefault,
  className = null,
  headingClassName = 'heading-5',
  inventoryData = null,
  selectedFacets = null,
  visitorLocation = null,
  location = null,
  setModelValue,
  zipCode = null,
  creativeId = CREATIVE_ID,
  hasLeadForm = false,
  hideEmpty = false,
  isMobile = false,
}) {
  const [isCollapsed, setCollapsed] = useState(true);

  const isInternational = isUnknownZip(visitorLocation);
  const { totalNumber: inventoryCount } = get(inventoryData, 'inventories', {});
  const displayInventories =
    get(inventoryData, 'inventories.results') &&
    inventoryData.inventories.results.slice(0, MINI_INVENTORY_COUNT[isMobile]);
  const facets = get(inventoryData, 'facets', []).filter(({ type }) => !excludedFilterTypes?.includes(type));

  const {
    filteringByFacets,
    radius,
    onUpdateFilter,
    onFiltersButtonClick,
    closeFiltersDrawer,
    cancelFiltersUpdate,
    updateZipCode,
    isFilterDrawerOpen,
    isLoading,
  } = useCoreSrpFilter({
    inventoryData,
    facets,
    selectedFacets,
    initialFilter,
    pathname: location.pathname,
    visitorLocation,
    addFiltersToQuery,
    setModelValue,
  });

  const firstPart = displayInventories && displayInventories.slice(0, DISPLAY_INVENTORY_COUNT[isMobile]);
  const secondPart = displayInventories && displayInventories.slice(DISPLAY_INVENTORY_COUNT[isMobile]);
  const displayInventoryCount = isCollapsed ? firstPart?.length : firstPart?.length + secondPart?.length;

  const firstPartVins = firstPart?.map(({ vin }) => vin).join();

  useEffect(() => {
    TrackingHandler.tryAddMap(MiniInventoryMap);
  }, []);

  useEffect(() => {
    if (firstPartVins) {
      EventToolbox.fireCustomEvent(ACTIONS.INVENTORY_LIST_VIEW, {
        ...getInventoryListTrackingData({
          radius,
          inventoryData,
          selectedFacets,
          isSrp: true,
          creativeId,
        }),
        inventory: firstPart,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstPartVins]);

  const secondPartVins = secondPart?.map(({ vin }) => vin).join();
  useEffect(() => {
    if (!isCollapsed) {
      EventToolbox.fireCustomEvent(ACTIONS.INVENTORY_LIST_VIEW, {
        ...getInventoryListTrackingData({
          radius,
          inventoryData,
          selectedFacets,
          creativeId,
        }),
        inventory: secondPart,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollapsed, secondPartVins]);

  const srpLink = selectedFacets && srpLinkBuilderWithParamsConversion(omit(selectedFacets, ['national']));

  if (!inventoryData || (hideEmpty && !inventoryCount)) {
    return null;
  }

  const cardProps = {
    hasVehicleParams: !!vehicleParams,
    initialFilter,
    radius,
    zipCode,
    isInternational,
    isMobile,
    hasLeadForm,
  };

  const vehicleDisplayName =
    vehicleName || compact([vehicleParams?.year, vehicleParams?.makeName, vehicleParams?.modelName]).join(' ');

  return (
    <div className={classnames('mini-inventory-module', className)} data-tracking-parent={creativeId}>
      <ScrollElement id="scrollTop-mini-inventory" />
      <div className="d-flex align-items-baseline mb-1">
        <span className={headingClassName}>
          {vehicleDisplayName ? <Fragment>{vehicleDisplayName} for sale</Fragment> : <Fragment>For sale</Fragment>}{' '}
          {!isInternational && 'near you'}
        </span>

        {isInternational ? (
          <StyledZipInput
            styledWrapperClasses="international-zip-input ms-1"
            onValidate={validation.validateZip}
            onZipChange={updateZipCode}
            getTracking={{ 'data-tracking-id': 'provide_zip_code' }}
            forceEmptyZip
            placeholder="Zip code"
            isMobile={isMobile}
            inputSize=""
            inputClasses="ps-2 py-0_5 pe-0_5 size-16 rounded-8"
          />
        ) : (
          <Zip
            zip={zipCode}
            onUpdate={updateZipCode}
            buttonClassName="size-16 ms-0_5 py-0_25"
            zipClassName="text-blue-40 fw-normal text-underline"
            showDropdownIcon={false}
            buttonTrackingId="provide_zip_code"
          />
        )}
      </div>

      <div className="d-flex align-items-center mb-1">
        <FilterButton
          classes=""
          onFiltersBtnClick={onFiltersButtonClick}
          activeFiltersCount={transformSelectedFacetsForDisplaying(filteringByFacets).length}
          trackingId="view_mini_inventory_filters"
        />
        <div className="medium text-cool-gray-40 ms-0_5" aria-live="polite" role="status" aria-atomic="true">
          <span className="visually-hidden">Showing&nbsp;</span>
          {displayInventoryCount} of {inventoryCount} listings
        </div>
      </div>

      <LoadingSpinner isLoading={isLoading}>
        {!!inventoryCount && (
          <Fragment>
            {!!firstPart?.length && (
              <Row tag="ul" className="list-unstyled mb-0">
                {firstPart.map(inventory => (
                  <Col key={inventory.vin} tag="li" xs={6} md={4} className="mb-1_5">
                    <MiniInventoryCard {...cardProps} inventoryVehicle={inventory} />
                  </Col>
                ))}
              </Row>
            )}
            {!!secondPart?.length && (
              <ContentCollapse
                minHeight="0px"
                textClosed="Show more"
                textOpen="Show less"
                ariaLabelHeading="available vehicles"
                justify="justify-content-start"
                btnClasses="px-0_5 py-0 text-blue-40"
                btnContainerClasses="py-0 mb-1_5"
                btnTextClasses="fw-normal size-16"
                btnTrackingId="show_more_mini_inventory"
                isSameExpandCollapseTracking
                isOnlyExpandTracking
                onToggleContentCollapseState={setCollapsed}
                showBtnBelow
                resetParams={{
                  offset: SCROLL_OFFSET,
                  duration: SCROLL_DURATION,
                  to: 'scrollTop-mini-inventory',
                }}
              >
                <Row tag="ul" className="list-unstyled mb-0_5" data-tracking-parent="edm-entry-vehicle-card-list">
                  {secondPart.map(inventory => (
                    <Col key={inventory.vin} tag="li" xs={6} md={4} className="mb-1_5">
                      <MiniInventoryCard {...cardProps} inventoryVehicle={inventory} />
                    </Col>
                  ))}
                </Row>
              </ContentCollapse>
            )}
          </Fragment>
        )}

        {!inventoryCount && (
          <div className="mb-2">Sorry, there are no {vehicleName || 'matching'} vehicles for sale in our network.</div>
        )}

        <Button
          tag={Link}
          color="blue-50"
          to={srpLink}
          rel="nofollow"
          className="px-1_5 py-0_5 rounded-8 text-transform-none fw-medium"
          data-tracking-id="mini_inventory_srp_link"
        >
          {inventoryCount ? (
            <Fragment>
              See all {inventoryCount} {getPlural('listing', inventoryCount)}
            </Fragment>
          ) : (
            <Fragment>See all for sale</Fragment>
          )}
        </Button>
      </LoadingSpinner>

      <InventoryDependencyContext.Provider value={usurpDependencyContextValue}>
        <UsurpFiltersDrawer
          isOpen={isFilterDrawerOpen}
          onToggle={closeFiltersDrawer}
          onCancel={cancelFiltersUpdate}
          onApply={closeFiltersDrawer}
          applyLink={srpLink}
          searchResultsFound={inventoryCount}
          isLoading={isLoading}
          dataTrackingParent="mini-inventory-drawer-facets"
          drawerContainerClasses="mini-inventory-filters-drawer"
          isMobile={isMobile}
          filtersComponent={UsurpFilters}
          paymentType={getSelectedPaymentType(selectedFacets)}
          facets={facets}
          filteringByFacets={filteringByFacets}
          onUpdate={onUpdateFilter}
          zipCode={zipCode}
          onZipCodeUpdate={updateZipCode}
          selectedFacets={selectedFacets}
        />
      </InventoryDependencyContext.Provider>
    </div>
  );
}

MiniInventoryModuleUI.propTypes = {
  vehicleParams: PropTypes.shape({
    makeName: PropTypes.string,
    modelName: PropTypes.string,
    makeSlug: PropTypes.string,
    modelSlug: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  vehicleName: PropTypes.string,
  initialFilter: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]))
    .isRequired,
  setModelValue: PropTypes.func.isRequired,
  addFiltersToQuery: PropTypes.func,
  excludedFilterTypes: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
  headingClassName: PropTypes.string,
  inventoryData: PropTypes.shape({
    inventories: PropTypes.shape({
      results: InventoryEntities.InventoriesVin,
      totalNumber: PropTypes.number,
      totalPages: PropTypes.number,
    }),
    facets: InventoryEntities.Facets,
  }),
  selectedFacets: PropTypes.shape({}),
  visitorLocation: VisitorEntities.Location,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
  zipCode: PropTypes.string,
  creativeId: PropTypes.string,
  hasLeadForm: PropTypes.bool,
  hideEmpty: PropTypes.bool,
  isMobile: PropTypes.bool,
};

export const propsAreEqual = (prevProps, { inventoryData }) => !inventoryData;
export const MiniInventoryModule = flow(component =>
  connectToModel(component, {
    zipCode: bindToPath('location.zipCode', VisitorModel),
    visitorLocation: bindToPath('location', VisitorModel, null, false),
    location: bindToPath('location', PageModel),
    inventoryData: bindToPath('searchResults', InventoryModel, null, false),
    selectedFacets: bindToPath('searchResultsFilter', InventoryModel, null, false),
  })
)(React.memo(MiniInventoryModuleUI, propsAreEqual));
